import { NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EventEmitter, Component, forwardRef, Renderer2, Input, Output, ViewChild, Directive, ElementRef, HostListener, NgModule } from '@angular/core';
import { formatNumber, CommonModule } from '@angular/common';

import * as ɵngcc0 from '@angular/core';

const _c0 = ["inputElement"];
var NumericControlTypes;
(function (NumericControlTypes) {
    NumericControlTypes[NumericControlTypes["Integer"] = 1] = "Integer";
    NumericControlTypes[NumericControlTypes["Float"] = 2] = "Float";
    NumericControlTypes[NumericControlTypes["Currency"] = 3] = "Currency";
    NumericControlTypes[NumericControlTypes["Percentage"] = 4] = "Percentage";
})(NumericControlTypes || (NumericControlTypes = {}));
/**
 * rounds a number.
 * @param {number} n the number to round
 * @param {number} decimals the precision to round to
 * @return {number} the result
 */
function round(n, decimals) {
    return Number(Math.round(Number(n + 'e' + decimals)) + 'e-' + decimals);
}
/**
 * class used to get default values for ConvertNumberFunctions
 */
class DefaultConversionFunctions {
    static fromDisplayToForm(type) {
        if (type === NumericControlTypes.Percentage) {
            return (value, type, scale) => {
                return value ? round(value / 100, scale + 2) : null;
            };
        }
    }
    static fromFormToDisplay(type) {
        if (type === NumericControlTypes.Percentage) {
            return (value, type, scale) => {
                return value ? round(value * 100, scale) : null;
            };
        }
    }
}
function getDefaultNumberFormatDisplayFunction() {
    return (value, type, scale) => {
        return formatNumber(value, 'en-us', `${1}.${scale}-${scale}`);
    };
}

/**
 * This component is a Reactive Forms ready control that can be set within a FromGroup and assigned a
 * formControlName property to be hooked into a Form.  In order to act like a input type=number while
 * editing but then be able to display text like a input type=input, it changes its type on blur and
 * focus.
 */
class NumericComponent {
    constructor(_renderer) {
        this._renderer = _renderer;
        this.disabled = false;
        // *** END -- CONTROL VALUE ACCESSOR ***
        this.type = NumericControlTypes.Integer;
        this.placeholder = null;
        this.doNotAllowZero = false;
        this.blur = new EventEmitter();
        this.focus = new EventEmitter();
        this.ready = new EventEmitter();
    }
    // *** CONTROL VALUE ACCESSOR ***
    /* tslint:disable:member-ordering */
    writeValue(obj) {
        this._updateValueInView(obj);
    }
    registerOnChange(fn) {
        this._onValueChanged = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    ngOnInit() {
        if (typeof this.scale === 'undefined' || this.scale === null) {
            this.scale = this.type === NumericControlTypes.Currency ? 2 : 0;
        }
        if (typeof this.numberFormatDisplayFunction === 'undefined') {
            this.numberFormatDisplayFunction = getDefaultNumberFormatDisplayFunction();
        }
        if (typeof this.convertFromDisplayToFormFunction === 'undefined') {
            this.convertFromDisplayToFormFunction = DefaultConversionFunctions.fromDisplayToForm(this.type);
        }
        if (typeof this.convertFromFormToDisplayFunction === 'undefined') {
            this.convertFromFormToDisplayFunction = DefaultConversionFunctions.fromFormToDisplay(this.type);
        }
        this.ready.emit({
            focus: this.focusMe.bind(this),
        });
    }
    focusMe() {
        var _a, _b, _c;
        (_c = (_b = (_a = this.inputElement) === null || _a === void 0 ? void 0 : _a.nativeElement) === null || _b === void 0 ? void 0 : _b.focus) === null || _c === void 0 ? void 0 : _c.call(_b);
    }
    onBlur() {
        var _a;
        this.blur.emit();
        (_a = this._onTouched) === null || _a === void 0 ? void 0 : _a.call(this);
        this.setFormValue();
    }
    /**
     * gets the value from the input type input element, scrubs it to have only number characters, sets
     * the input type of the input element to be number, and sets the scrubbed value.  Also emits a
     * focus event.
     */
    onFocus() {
        var _a, _b;
        this._changeInputToNumberType();
        (_b = (_a = this.inputElement.nativeElement).select) === null || _b === void 0 ? void 0 : _b.call(_a);
        this.focus.emit();
    }
    _changeInputToNumberType() {
        let value = this.inputElement.nativeElement.value;
        value = value.replace(/[^0-9.-]/g, '');
        this._renderer.setProperty(this.inputElement.nativeElement, 'type', 'number');
        this._renderer.setProperty(this.inputElement.nativeElement, 'value', value);
    }
    setFormValue() {
        var _a;
        const value = this._getFormValueFromInput();
        if (value !== this.value) {
            this.value = value;
            (_a = this._onValueChanged) === null || _a === void 0 ? void 0 : _a.call(this, value);
        }
        this._updateValueInView(value);
    }
    /**
     * takes in a form value of number, performs any manipulation of the value from what is stored in the form to what is
     * shown in the view (i.e. percentage shows as 27 but gets stored as .27) and gets the value as a formatted string.
     * Sets the input type of the input element to be input, and sets the string value.
     */
    _updateValueInView(value) {
        this.value = value;
        // run the conversion function if provided
        if (this.convertFromFormToDisplayFunction) {
            value = this.convertFromFormToDisplayFunction(value, this.type, this.scale);
        }
        this._changeInputToTextType(value);
    }
    _changeInputToTextType(value) {
        let valueToShow = this._getValueToShow(value);
        this._renderer.setProperty(this.inputElement.nativeElement, 'type', 'input');
        this._renderer.setProperty(this.inputElement.nativeElement, 'value', valueToShow);
    }
    /**
     * using the value from the form, get a string version to show in the view.
     * @returns string
     */
    _getValueToShow(value) {
        if (this.numberFormatDisplayFunction) {
            return this.numberFormatDisplayFunction(value, this.type, this.scale);
        }
        else {
            return value === null || value === void 0 ? void 0 : value.toString();
        }
    }
    /**
     * get the value from the input element, validate it as a number and round using scale.  Also perform
     * any manipulation of the value from what is in the view to what is stored in the form (i.e. percentage
     * shows as 27 but gets stored as .27)
     * @returns number
     */
    _getFormValueFromInput() {
        let value = this._validateValueFromInputAndRound(this.inputElement.nativeElement.value);
        // run the conversion function if provided
        if (this.convertFromDisplayToFormFunction) {
            value = this.convertFromDisplayToFormFunction(value, this.type, this.scale);
        }
        return value;
    }
    _validateValueFromInputAndRound(value) {
        if (isNaN(value)) {
            value = null;
        }
        else if (typeof value === 'string') {
            const isEmptyString = value.trim() === '';
            if (isEmptyString) {
                value = null;
            }
            else {
                value = +value; // convert to number
            }
        }
        if (value) {
            value = round(value, this.scale);
        }
        if ((!value && value !== 0) || (this.doNotAllowZero && value === 0)) {
            value = null;
        }
        return value;
    }
}
NumericComponent.ɵfac = function NumericComponent_Factory(t) { return new (t || NumericComponent)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.Renderer2)); };
NumericComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: NumericComponent, selectors: [["mt-numeric"]], viewQuery: function NumericComponent_Query(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵstaticViewQuery(_c0, true);
    } if (rf & 2) {
        var _t;
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.inputElement = _t.first);
    } }, inputs: { type: "type", placeholder: "placeholder", doNotAllowZero: "doNotAllowZero", scale: "scale", numberFormatDisplayFunction: "numberFormatDisplayFunction", convertFromDisplayToFormFunction: "convertFromDisplayToFormFunction", convertFromFormToDisplayFunction: "convertFromFormToDisplayFunction" }, outputs: { blur: "blur", focus: "focus", ready: "ready" }, features: [ɵngcc0.ɵɵProvidersFeature([
            {
                multi: true,
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => NumericComponent)
            },
        ])], decls: 3, vars: 2, consts: [["type", "input", "mtNumbersOnly", "", 1, "form-control", 3, "blur", "focus", "keydown.enter"], ["inputElement", ""]], template: function NumericComponent_Template(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵelementStart(0, "input", 0, 1);
        ɵngcc0.ɵɵlistener("blur", function NumericComponent_Template_input_blur_0_listener() { return ctx.onBlur(); })("focus", function NumericComponent_Template_input_focus_0_listener() { return ctx.onFocus(); })("keydown.enter", function NumericComponent_Template_input_keydown_enter_0_listener() { return ctx.setFormValue(); });
        ɵngcc0.ɵɵelementEnd();
        ɵngcc0.ɵɵtext(2, "\n");
    } if (rf & 2) {
        ɵngcc0.ɵɵattribute("placeholder", ctx.placeholder)("disabled", ctx.disabled ? "disabled" : null);
    } }, directives: function () { return [MtNumbersOnlyDirective]; }, encapsulation: 2 });
NumericComponent.ctorParameters = () => [
    { type: Renderer2 }
];
NumericComponent.propDecorators = {
    type: [{ type: Input }],
    scale: [{ type: Input }],
    placeholder: [{ type: Input }],
    doNotAllowZero: [{ type: Input }],
    numberFormatDisplayFunction: [{ type: Input }],
    convertFromDisplayToFormFunction: [{ type: Input }],
    convertFromFormToDisplayFunction: [{ type: Input }],
    blur: [{ type: Output }],
    focus: [{ type: Output }],
    ready: [{ type: Output }],
    inputElement: [{ type: ViewChild, args: ['inputElement', { static: true },] }]
};
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(NumericComponent, [{
        type: Component,
        args: [{
                providers: [
                    {
                        multi: true,
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => NumericComponent)
                    },
                ],
                selector: 'mt-numeric',
                template: "<input\n    #inputElement\n    type=\"input\"\n    mtNumbersOnly\n    (blur)=\"onBlur()\"\n    (focus)=\"onFocus()\"\n    [attr.placeholder]=\"placeholder\"\n    [attr.disabled]=\"disabled ? 'disabled' : null\"\n    class=\"form-control\"\n    (keydown.enter)=\"setFormValue()\"\n/>\n"
            }]
    }], function () { return [{ type: ɵngcc0.Renderer2 }]; }, { type: [{
            type: Input
        }], placeholder: [{
            type: Input
        }], doNotAllowZero: [{
            type: Input
        }], blur: [{
            type: Output
        }], focus: [{
            type: Output
        }], ready: [{
            type: Output
        }], scale: [{
            type: Input
        }], numberFormatDisplayFunction: [{
            type: Input
        }], convertFromDisplayToFormFunction: [{
            type: Input
        }], convertFromFormToDisplayFunction: [{
            type: Input
        }], inputElement: [{
            type: ViewChild,
            args: ['inputElement', { static: true }]
        }] }); })();

class MtNumbersOnlyDirective {
    constructor(el) {
        this.el = el;
        // Allow decimal numbers. The \. is only allowed once to occur
        this.regex = new RegExp(/-?\d*\.?\d*$/g);
        // Allow key codes for special events.
        this.specialKeys = ['Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Delete'];
    }
    onKeyDown(event) {
        var _a;
        // Allow Backspace, tab, end, and home keys
        if (this.specialKeys.indexOf(event.key) !== -1) {
            return;
        }
        // get the current value
        let current = this.el.nativeElement.value;
        if (event.key === '-') {
            // negative sign character is handled differently
            if (current.indexOf('-') !== -1) {
                // already has a negative sign, so prevent and return
                event.preventDefault();
                return;
            }
            else if ((_a = this.el.nativeElement.validity) === null || _a === void 0 ? void 0 : _a.badInput) {
                // if currently bad input, then we already have a negative sign
                // we just don't see it in the current value because of badInput
                event.preventDefault();
                return;
            }
            else if (current) {
                // we have a valid current value that is not empty and
                // does not already have a negative sign so
                // prevent default and prepend to beginning of string
                event.preventDefault();
                this.el.nativeElement.value = '-' + current;
            }
            else {
                // let it through
                return;
            }
        }
        else {
            // everything else handled like the following
            // ------------------------------------------
            // add the key pressed to that value
            // note: this is not done correctly, since we don't know where the cursor
            // was when this key was pressed, yet via concat we are just putting it on
            // the end
            let next = current.concat(event.key);
            // if not valid, then prevent it
            if (next && !String(next).match(this.regex)) {
                event.preventDefault();
            }
        }
    }
}
MtNumbersOnlyDirective.ɵfac = function MtNumbersOnlyDirective_Factory(t) { return new (t || MtNumbersOnlyDirective)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef)); };
MtNumbersOnlyDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: MtNumbersOnlyDirective, selectors: [["", "mtNumbersOnly", ""]], hostBindings: function MtNumbersOnlyDirective_HostBindings(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵlistener("keydown", function MtNumbersOnlyDirective_keydown_HostBindingHandler($event) { return ctx.onKeyDown($event); });
    } } });
MtNumbersOnlyDirective.ctorParameters = () => [
    { type: ElementRef }
];
MtNumbersOnlyDirective.propDecorators = {
    onKeyDown: [{ type: HostListener, args: ['keydown', ['$event'],] }]
};
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(MtNumbersOnlyDirective, [{
        type: Directive,
        args: [{
                selector: '[mtNumbersOnly]'
            }]
    }], function () { return [{ type: ɵngcc0.ElementRef }]; }, { onKeyDown: [{
            type: HostListener,
            args: ['keydown', ['$event']]
        }] }); })();

class NumericControlModule {
}
NumericControlModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: NumericControlModule });
NumericControlModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function NumericControlModule_Factory(t) { return new (t || NumericControlModule)(); }, imports: [[CommonModule, FormsModule, ReactiveFormsModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(NumericControlModule, { declarations: function () { return [NumericComponent, MtNumbersOnlyDirective]; }, imports: function () { return [CommonModule, FormsModule, ReactiveFormsModule]; }, exports: function () { return [NumericComponent, MtNumbersOnlyDirective]; } }); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(NumericControlModule, [{
        type: NgModule,
        args: [{
                declarations: [NumericComponent, MtNumbersOnlyDirective],
                exports: [NumericComponent, MtNumbersOnlyDirective],
                imports: [CommonModule, FormsModule, ReactiveFormsModule]
            }]
    }], null, null); })();

/**
 * Generated bundle index. Do not edit.
 */

export { DefaultConversionFunctions, MtNumbersOnlyDirective, NumericComponent, NumericControlModule, NumericControlTypes, getDefaultNumberFormatDisplayFunction, round };

//# sourceMappingURL=mt-ng2-numeric-control.js.map